import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface Notif {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

const UNREAD_KEY = ['notifications.unread-count'];
const LIST_KEY = ['notifications.list'];

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'agora';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * Sino de notificações in-app (central por usuário). Badge com não-lidas
 * (poll a cada 30s), painel com a lista, marcar lida ao clicar (+ deep-link
 * via `data.url`), e "marcar todas".
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  const { data: countData } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: async () =>
      (await api.get('/me/notifications/unread-count')).data as {
        count: number;
      },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const unread = countData?.count ?? 0;

  const { data: items, isLoading } = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () =>
      (await api.get('/me/notifications', { params: { limit: 20 } }))
        .data as Notif[],
    enabled: open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: UNREAD_KEY });
    qc.invalidateQueries({ queryKey: LIST_KEY });
  };

  const markRead = useMutation({
    mutationFn: (ids: string[]) => api.post('/me/notifications/read', { ids }),
    onSuccess: invalidate,
  });
  const markAll = useMutation({
    mutationFn: () => api.post('/me/notifications/read-all'),
    onSuccess: invalidate,
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function onItemClick(n: Notif) {
    if (!n.readAt) markRead.mutate([n.id]);
    const url = typeof n.data?.url === 'string' ? (n.data.url as string) : null;
    setOpen(false);
    if (url) nav(url);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
        aria-label={`Notificações${unread ? ` (${unread} não lidas)` : ''}`}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-md text-ink-500 hover:text-ink-800 hover:bg-ink-100 transition-colors"
        title="Notificações"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unread > 0 && (
          <span className="absolute top-1 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-xl border border-ink-200 bg-white shadow-pop z-30 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink-100">
            <span className="text-sm font-semibold text-ink-800">
              Notificações
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-xs text-brand-600 hover:text-brand-700 disabled:text-ink-300"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-sm text-ink-400">
                Carregando…
              </div>
            ) : !items || items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-ink-400">
                Nenhuma notificação.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={
                    'w-full text-left px-4 py-3 border-b border-ink-50 hover:bg-ink-50 transition-colors flex gap-3 ' +
                    (n.readAt ? '' : 'bg-brand-50/40')
                  }
                >
                  <span
                    className={
                      'mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ' +
                      (n.readAt ? 'bg-transparent' : 'bg-brand-500')
                    }
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink-800 truncate">
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="block text-xs text-ink-500 truncate">
                        {n.body}
                      </span>
                    )}
                    <span className="block text-[11px] text-ink-400 mt-0.5">
                      {timeAgo(n.createdAt)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
